import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { AttestationKind } from "@graftus/schema";
import type { SectorWords } from "@/hooks/use-sector-words";
import type { InviteSlot } from "@/lib/onboarding/slots";
import { sendBlocker } from "@/lib/onboarding/readiness";
import { AiMark } from "@/components/ui/ai-mark";
import { StepHeading } from "../OnboardingShell";
import { StepFooter } from "../StepFooter";
import { AttestationList } from "../ui/AttestationList";
import { EmailPreview } from "../ui/EmailPreview";
import { ScheduleChooser } from "../ui/ScheduleChooser";
import { focusRing } from "../ui/buttons";

export interface InviteRecipient {
  memberId: string;
  name: string;
  email: string;
}

export type GreetingSource = "ai" | "manual" | "fallback" | null;

interface InviteStepProps {
  words: SectorWords;
  recipients: InviteRecipient[];
  teamsCount: number;
  fromName: string;
  unitName: string;
  greeting: string;
  greetingSource: GreetingSource;
  greetingLoading: boolean;
  onGreetingChange: (text: string) => void;
  when: "now" | "later";
  onWhenChange: (when: "now" | "later") => void;
  slots: InviteSlot[];
  scheduledAt: string | null;
  onScheduledAtChange: (iso: string) => void;
  reminders: boolean;
  onRemindersChange: (on: boolean) => void;
  required: readonly AttestationKind[];
  attested: Partial<Record<AttestationKind, boolean>>;
  onAttestedChange: (kind: AttestationKind, checked: boolean) => void;
  onSend: () => void;
  sending: boolean;
  error: string | null;
  onBack?: () => void;
}

/** Step 6: the email preview, the manager's greeting, when to send, reminders and the attestation gate. */
export function InviteStep(props: InviteStepProps) {
  const { t } = useTranslation("onboarding");
  const { words, recipients, teamsCount } = props;
  const first = recipients[0];
  const blocker = sendBlocker({
    attested: props.attested,
    required: props.required,
    when: props.when,
    scheduledAt: props.scheduledAt,
    recipientCount: recipients.length,
  });
  const slotLabel = props.slots.find((s) => s.iso === props.scheduledAt)?.label ?? "";
  const primaryLabel =
    props.when === "now"
      ? t("invite.send.now", { n: recipients.length })
      : t("invite.send.later", { n: recipients.length, slot: slotLabel });

  return (
    <div data-testid="onboarding-invite">
      <StepHeading
        title={t("invite.title")}
        lead={first ? t("invite.lead", { first: first.name.split(/\s+/)[0] }) : t("invite.noRecipients")}
      />
      <p className="mb-4 text-[13.5px] font-medium text-g-ink-600">
        {t("invite.recipients", { n: recipients.length, teams: teamsCount })}
      </p>

      {first ? (
        <EmailPreview
          words={words}
          toName={first.name}
          toEmail={first.email}
          fromName={props.fromName}
          unitName={props.unitName}
          greeting={props.greeting}
        />
      ) : null}

      <div className="mt-5 flex flex-col gap-1.5">
        <label htmlFor="invite-greeting" className="flex items-center gap-2 text-[13px] font-semibold text-g-ink-700">
          {t("invite.greeting.label")}
          {props.greetingSource === "ai" ? (
            <span className="inline-flex items-center gap-1 text-[12px] font-medium text-g-primary">
              <AiMark />
              {t("invite.greeting.aiSuggested")}
            </span>
          ) : null}
          {props.greetingLoading ? (
            <span className="inline-flex items-center gap-1 text-[12px] font-medium text-g-ink-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              {t("invite.greeting.loading")}
            </span>
          ) : null}
        </label>
        <textarea
          id="invite-greeting"
          rows={4}
          value={props.greeting}
          onChange={(e) => props.onGreetingChange(e.currentTarget.value)}
          placeholder={t("invite.greeting.placeholder")}
          className={`w-full rounded-g-md border border-g-line bg-g-bg-input px-3.5 py-3 text-[15px] leading-relaxed text-g-ink-900 transition-colors placeholder:text-g-ink-400 hover:border-g-line-strong ${focusRing}`}
        />
      </div>

      <div className="mt-6">
        <ScheduleChooser
          when={props.when}
          onWhenChange={props.onWhenChange}
          slots={props.slots}
          scheduledAt={props.scheduledAt}
          onScheduledAtChange={props.onScheduledAtChange}
        />
      </div>

      <label className="mt-5 flex cursor-pointer items-center justify-between gap-4 rounded-g-lg border border-g-line bg-g-bg-card p-4">
        <span className="flex flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-g-ink-900">{t("invite.reminders.title")}</span>
          <span className="text-[12.5px] text-g-ink-500">{t("invite.reminders.body")}</span>
        </span>
        <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
          <input
            type="checkbox"
            role="switch"
            aria-checked={props.reminders}
            aria-label={t("invite.reminders.title")}
            checked={props.reminders}
            onChange={(e) => props.onRemindersChange(e.currentTarget.checked)}
            className={`peer h-6 w-11 cursor-pointer appearance-none rounded-g-pill bg-g-line-strong transition-colors checked:bg-g-primary ${focusRing}`}
          />
          <span className="pointer-events-none absolute left-0.5 h-5 w-5 rounded-g-pill bg-g-bg-card shadow-g-card transition-transform peer-checked:translate-x-5" aria-hidden="true" />
        </span>
      </label>

      <div className="mt-6">
        <AttestationList required={props.required} attested={props.attested} onChange={props.onAttestedChange} />
      </div>

      {props.error ? (
        <p className="mt-5 rounded-g-md border border-g-danger bg-g-danger-tint px-4 py-3 text-[14px] font-medium text-g-danger" role="alert">
          {props.error}
        </p>
      ) : null}

      <StepFooter
        primaryLabel={primaryLabel}
        onPrimary={props.onSend}
        primaryDisabled={blocker !== null}
        loading={props.sending}
        hint={blocker ? t(`invite.hint.${blocker}`) : null}
        onBack={props.onBack}
      />
    </div>
  );
}
